import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown, Folder, Check, Loader2, Github } from "lucide-react";
import { api, type Project, type GitHubStatus } from "@/lib/api";
import { StatusDot } from "@/components/ui/status-dot";
import { cn } from "@/lib/utils";

interface ProjectSwitcherProps {
  currentProject: Project | null;
}

export function ProjectSwitcher({ currentProject }: ProjectSwitcherProps) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [projects, setProjects] = useState<Project[]>([]);
  const [githubStatus, setGithubStatus] = useState<GitHubStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Load project list + GitHub status on open
  const loadData = useCallback(async () => {
    if (loaded) return;
    setLoading(true);
    try {
      const [list, ghStatus] = await Promise.all([
        api.projects.list(),
        api.github.status().catch(() => null),
      ]);
      setProjects(list);
      if (ghStatus) setGithubStatus(ghStatus);
      setLoaded(true);
    } catch (err) {
      console.error("Failed to load projects:", err);
    } finally {
      setLoading(false);
    }
  }, [loaded]);

  function toggle() {
    const next = !open;
    setOpen(next);
    if (next) {
      // Refetch on every open to catch OAuth completions in other tabs
      setLoaded(false);
      loadData();
    }
  }

  function selectProject(project: Project) {
    setOpen(false);
    if (project.id !== currentProject?.id) {
      navigate(`/chat/${project.id}`);
    }
  }

  // Close on click outside
  useEffect(() => {
    if (!open) return;

    function handleClick(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;

    function handleKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }

    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [open]);

  return (
    <div ref={dropdownRef} className="relative flex-1 min-w-0">
      {/* Trigger */}
      <button
        onClick={toggle}
        className={cn(
          "flex items-center gap-1.5 min-w-0 rounded-lg px-2 py-1 -mx-2",
          "transition-colors duration-fast",
          "hover:bg-intri-raised",
          open && "bg-intri-raised",
        )}
      >
        <span className="text-sm font-medium text-intri-text truncate">
          {currentProject?.name ?? "intri.ai"}
        </span>
        <ChevronDown
          size={14}
          className={cn(
            "text-intri-muted shrink-0 transition-transform duration-fast",
            open && "rotate-180",
          )}
        />
      </button>

      {/* Dropdown */}
      {open && (
        <div
          className={cn(
            "absolute top-full left-0 mt-1 w-[calc(100vw-2rem)] sm:w-64",
            "bg-intri-surface border border-intri-border rounded-xl",
            "shadow-intri-lg",
            "z-intri-dropdown",
            "animate-fade-in",
            "overflow-hidden",
          )}
        >
          {loading ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 size={16} className="text-intri-dim animate-spin" />
            </div>
          ) : projects.length === 0 ? (
            <div className="px-4 py-6 text-center">
              <p className="text-sm text-intri-muted">No projects</p>
            </div>
          ) : (
            <ul className="py-1 max-h-64 overflow-y-auto">
              {projects.map((project) => {
                const isCurrent = project.id === currentProject?.id;

                return (
                  <li key={project.id}>
                    <button
                      onClick={() => selectProject(project)}
                      className={cn(
                        "w-full flex items-center gap-3 px-3 py-2.5 text-left",
                        "transition-colors duration-fast",
                        isCurrent
                          ? "bg-intri-accent-ghost text-intri-text"
                          : "text-intri-dim hover:bg-intri-raised hover:text-intri-text",
                      )}
                    >
                      <Folder
                        size={14}
                        className={cn(
                          "shrink-0",
                          isCurrent ? "text-intri-accent" : "text-intri-muted",
                        )}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">
                          {project.name}
                        </p>
                        {project.description && (
                          <p className="text-2xs text-intri-muted truncate mt-0.5">
                            {project.description}
                          </p>
                        )}
                      </div>
                      {isCurrent && (
                        <Check size={14} className="text-intri-accent shrink-0" />
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}

          {/* GitHub footer */}
          {!loading && (
            <div className="border-t border-intri-border px-3 py-2.5">
              {githubStatus?.connected ? (
                <div className="flex items-center gap-2 text-xs text-intri-dim">
                  <Github size={12} className="text-intri-muted shrink-0" />
                  <span className="truncate">
                    {githubStatus.installations[0]?.account_login}
                  </span>
                  <StatusDot status="connected" size="sm" />
                </div>
              ) : (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    window.open(api.github.oauthUrl(), "_blank");
                    setOpen(false);
                  }}
                  className={cn(
                    "flex items-center gap-2 w-full text-xs",
                    "text-intri-muted hover:text-intri-accent transition-colors duration-fast",
                  )}
                >
                  <Github size={12} />
                  <span>Connect GitHub</span>
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
